/**
 * features/textbooks/PupilBooks.tsx
 * ------------------------------------------------------------------
 * One pupil's school textbooks — the parent portal's Books tab and the
 * pupil's own "My books". Read straight from textbookLoans by studentId; the
 * rules let only the child's parent, the pupil and staff do that.
 *
 * Deliberately NOT behind the fees hold: a book on loan is something the
 * family owes, not an academic privilege (the school's decision, 30 Oct 2026).
 * ------------------------------------------------------------------ */
import { BookOpen } from 'lucide-react';
import { fmtPula } from '@/lib/financeCore';
import { conditionLabel, isOverdue, type TextbookLoan } from '@/lib/textbooks';
import { usePupilLoans } from './textbookStore';

const longDate = (iso?: string) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

function Row({ l, own }: { l: TextbookLoan; own: boolean }) {
  const overdue = isOverdue(l, new Date().toISOString());
  const tone = l.status === 'out'
    ? overdue ? 'border-l-rose-500' : 'border-l-sky-500'
    : 'border-l-amber-500';
  return (
    <li className={`rounded-r-lg border border-gray-100 dark:border-gray-700 border-l-4 ${tone} bg-white dark:bg-gray-800 px-4 py-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-gray-900 dark:text-white">{l.title}</p>
        <span className="text-xs text-gray-500">Copy {l.copyNo}</span>
      </div>
      <p className="text-xs text-gray-500 mt-0.5">
        {[l.subjectName, `issued ${longDate(l.issuedAt)} in ${conditionLabel(l.conditionOut).toLowerCase()} condition`].filter(Boolean).join(' · ')}
      </p>
      {l.status === 'out' ? (
        <p className={`text-sm mt-1 ${overdue ? 'text-rose-700 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
          {l.dueDate ? `${overdue ? 'Overdue — was due' : 'Due back'} ${longDate(l.dueDate)}` : 'On loan'}
        </p>
      ) : (
        <p className="text-sm mt-1 text-amber-700">
          {l.status === 'lost' ? 'Recorded as not returned' : 'Returned too damaged to use again'}
          {l.bursary === 'charged' && l.charge ? ` — replacement of ${fmtPula(l.charge.amount)} invoiced (${l.charge.invoiceNo}).`
            : l.bursary === 'waived' ? ' — no charge.'
            : own ? ' — please speak to your teacher.' : ' — the bursary will be in touch.'}
        </p>
      )}
    </li>
  );
}

export function PupilBooks({ studentId, own = false }: { studentId: string | undefined; own?: boolean }) {
  const { rows, ready, err } = usePupilLoans(studentId);
  // Out, and lost/ruined still being settled or charged; a returned book is done.
  const current = rows
    .filter((l) => l.status !== 'returned')
    .sort((a, b) => Number(b.status === 'out') - Number(a.status === 'out') || a.title.localeCompare(b.title));
  const back = rows.filter((l) => l.status === 'returned').length;

  if (!ready) return <p className="text-sm text-gray-400 py-6">Loading books…</p>;
  if (err) return <p className="text-sm text-rose-600 py-6">Could not load books: {err}</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BookOpen size={18} className="text-primary" />
        <h3 className="font-bold text-gray-900 dark:text-white">{own ? 'My school books' : 'School books on loan'}</h3>
      </div>
      {current.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          {own ? 'You have no school books on loan.' : 'No school books are on loan to your child.'}
          {back > 0 && ` ${back} returned.`}
        </p>
      ) : (
        <>
          <ul className="space-y-2">{current.map((l) => <Row key={l.id} l={l} own={own} />)}</ul>
          <p className="text-xs text-gray-400">
            {own ? 'Look after these — a lost or ruined book is charged to your family.'
              : 'Books are lent for the school year. A lost or ruined book is charged at its replacement cost.'}
            {back > 0 && ` ${back} returned book${back > 1 ? 's' : ''} not shown.`}
          </p>
        </>
      )}
    </div>
  );
}
